import { useAuth } from '../store/auth';

const BASE = import.meta.env.VITE_API_URL;

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API ${status}`);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Set to true on /session/claim and /session/release — those are the
   *  endpoints that establish or release the X-Session-Id contract, so
   *  they must NOT short-circuit on `session_revoked`. */
  bypassSessionRevokedHandler?: boolean;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { idToken, sessionId } = useAuth.getState();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  if (sessionId) headers['X-Session-Id'] = sessionId;

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch {
    parsed = undefined;
  }

  if (!res.ok) {
    // FR-66: another device took this session, or an admin force-kicked
    // (FR-67). Flag globally — Layout / RouteGuard reacts to this by
    // signing out and bouncing to /login with a toast. We swallow the
    // error otherwise so the caller doesn't try to render a stale row.
    const body = parsed as { error?: string } | undefined;
    if (
      res.status === 401 &&
      body?.error === 'session_revoked' &&
      !opts.bypassSessionRevokedHandler
    ) {
      useAuth.getState().setSessionRevoked(true);
    }
    throw new ApiError(res.status, parsed);
  }
  return parsed as T;
}

export type AgentStatus = 'Available' | 'Away' | 'Offline' | 'In-call';

export interface UserListItem {
  userId: string;
  lumiUserId?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'admin' | 'agent';
  status: AgentStatus;
  createdAt: string;
}

export interface AgentPresence {
  userId: string;
  lumiUserId?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'admin' | 'agent';
  status: AgentStatus;
}

export interface QueueActiveCall {
  taskSid: string;
  callSid: string;
  from: string;
  to: string;
  direction: 'inbound' | 'outbound';
  agentEmail: string;
  agentFirstName?: string;
  agentLastName?: string;
  agentUserId: string;
  startedAt: string;
}

export interface QueueWaitingCall {
  taskSid: string;
  from: string;
  queuedAt: string;
}

export interface QueueResponse {
  active: QueueActiveCall[];
  waiting: QueueWaitingCall[];
}

export interface AgentsResponse { agents: AgentPresence[]; }

export interface CallListItem {
  agentUserId: string;
  startedAt: string;
  callSid: string;
  from: string;
  to: string;
  direction?: 'inbound' | 'outbound';
  durationSeconds?: number;
  status: 'in-progress' | 'completed' | 'no-answer' | 'failed';
  taskSid?: string;
}

/** Best-effort "other party" — the customer/destination number, regardless of direction. */
export function otherParty(call: { direction?: 'inbound' | 'outbound'; from: string; to: string }): string {
  return call.direction === 'outbound' ? call.to : call.from;
}

export interface CreateUserBody {
  email: string;
  role: 'admin' | 'agent';
  firstName?: string;
  lastName?: string;
}

export interface UpdateUserBody {
  role?: 'admin' | 'agent';
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const api = {
  listUsers: () => request<{ users: UserListItem[] }>('/users'),
  createUser: (body: CreateUserBody) =>
    request<{ user: UserListItem }>('/users', { method: 'POST', body }),
  deleteUser: (userId: string) =>
    request<{ ok: true }>(`/users/${userId}`, { method: 'DELETE' }),
  updateUser: (userId: string, patch: UpdateUserBody) =>
    request<{ ok: true }>(`/users/${userId}`, { method: 'PUT', body: patch }),
  /** @deprecated use updateUser({ role }) — kept for legacy callers. */
  updateUserRole: (userId: string, role: 'admin' | 'agent') =>
    request<{ ok: true }>(`/users/${userId}`, { method: 'PUT', body: { role } }),
  resetUserPassword: (userId: string) =>
    request<{ tempPassword: string }>(`/users/${userId}/reset-password`, { method: 'POST' }),
  clearUserSession: (userId: string) =>
    request<{ ok: true }>(`/users/${userId}/clear-session`, { method: 'POST' }),
  /** FR-66 session claim. The two 409 cases are surfaced as ApiError so the
   *  caller can branch on body.error. */
  claimSession: (sessionId: string, force = false) =>
    request<{ ok: true; status: 'claimed' | 'continued'; tookOver?: boolean }>(
      '/session/claim',
      { method: 'POST', body: { sessionId, force }, bypassSessionRevokedHandler: true },
    ),
  releaseSession: () =>
    request<{ ok: true }>('/session/release', { method: 'POST', bypassSessionRevokedHandler: true }),
  setAgentStatus: (status: 'Available' | 'Away' | 'Offline') =>
    request<{ ok: true; status: string }>('/agent-status', { method: 'POST', body: { status } }),
  heartbeat: () => request<{ ok: true }>('/agent-heartbeat', { method: 'POST' }),
  listCalls: (agentId?: string) =>
    request<{ calls: CallListItem[] }>(`/calls${agentId ? `?agentId=${encodeURIComponent(agentId)}` : ''}`),
  getAgents: () => request<AgentsResponse>('/agents'),
  getQueue: () => request<QueueResponse>('/queue'),
  rejectCall: (taskSid: string) =>
    request<{ ok: true }>('/reject-call', { method: 'POST', body: { taskSid } }),
  transferCall: (callSid: string, targetUserId: string) =>
    request<{ ok: true }>('/transfer-call', { method: 'POST', body: { callSid, targetUserId } }),
  transferTargets: () =>
    request<{
      targets: {
        userId: string;
        email: string;
        firstName?: string;
        lastName?: string;
        role: 'admin' | 'agent';
      }[];
    }>('/transfer-targets'),
  missedCalls: () =>
    request<{ missed: { callSid: string; from: string; startedAt: string }[] }>('/missed-calls'),
  recentCalls: () =>
    request<{
      calls: {
        callSid: string;
        from: string;
        to: string;
        direction?: 'inbound' | 'outbound';
        agentUserId: string;
        agentEmail: string | null;
        agentFirstName?: string;
        agentLastName?: string;
        startedAt: string;
        durationSeconds?: number;
        status: 'in-progress' | 'completed' | 'no-answer' | 'failed';
      }[];
    }>('/recent-calls'),
};

/**
 * Best-effort sendBeacon to mark the agent Offline on tab close. Cannot use
 * the regular API client because navigator.sendBeacon can't set the
 * Authorization header — the JWT is in the body and the backend verifies
 * it manually against the Cognito JWKS.
 */
export function sendOfflineBeacon(idToken: string): void {
  const url = `${BASE}/agent-status-beacon`;
  const payload = JSON.stringify({ idToken, status: 'Offline' });
  // sendBeacon prefers a Blob; specifying the content-type avoids a CORS preflight.
  const blob = new Blob([payload], { type: 'application/json' });
  navigator.sendBeacon(url, blob);
}

